import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useRadio } from '../context/RadioContext';

/**
 * NowPlayingToast — pops a neon "Now Playing" notification every time the
 * Mindesign Radio auto-advances to a new track. Includes a quick ❤️ button
 * so visitors can heart the track without expanding the floating player.
 *
 * Triggers ONLY on auto-advance (when one track finishes and the next plays
 * naturally). Does NOT fire on:
 *   - Manual prev/next clicks
 *   - First Tune In
 *   - Quick re-plays after pause
 *
 * The auto-advance signal is the `mindesign:auto-advance` CustomEvent
 * dispatched from RadioContext's audio.ended handler.
 */
export const NowPlayingToast = () => {
  const { current, isCurrentLiked, toggleLikeCurrent } = useRadio();
  const armedRef = useRef(false);
  const lastShownIdRef = useRef(null);

  // Arm whenever the audio element fires 'ended' — the next render that
  // brings in a new `current` is the auto-advance, and that's when we toast.
  useEffect(() => {
    const arm = () => {
      armedRef.current = true;
    };
    window.addEventListener('mindesign:auto-advance', arm);
    return () => window.removeEventListener('mindesign:auto-advance', arm);
  }, []);

  useEffect(() => {
    if (!current?.id) return;
    if (!armedRef.current) return;
    if (lastShownIdRef.current === current.id) return;

    armedRef.current = false;
    lastShownIdRef.current = current.id;

    // Defer 400 ms so the new audio src has actually loaded before the toast pops
    const t = setTimeout(() => {
      toast(
        `${current.title}`,
        {
          description: `${current.artist || 'THE REDACTED'} · ${current.duration || ''} · ${current.genre || ''}`.trim(),
          duration: 5000,
          icon: '🎵',
          action: {
            label: isCurrentLiked ? '♥ Liked' : '♡ Like',
            onClick: () => {
              toggleLikeCurrent();
            },
          },
        }
      );
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  return null;
};

export default NowPlayingToast;
